import FormTextBox from "../yoello-lib/modules/dynamicForms/FormComponents/FormTextBox";
import FormAutoComplete from "../yoello-lib/modules/dynamicForms/FormComponents/FormAutoComplete";
import FormSelect from "../yoello-lib/modules/dynamicForms/FormComponents/FormSelect";
import ITimeZone from "../interfaces/ITimeZone";
import {
  ICurrency,
  ILanguage,
} from "../yoello-lib/modules/redux/actions/localization";
import IDynamicFormGroup from "../yoello-lib/modules/dynamicForms/interfaces/IDynamicFormGroup";
import { es, fr, enGB, enAU, enCA, enNZ, enUS } from 'date-fns/locale'
import { AlertStatusCard } from "../yoello-lib/modules/components/UI/Alerts/AlertStatusCard";
import { IMateriaulGridOps } from "../yoello-lib/modules/dynamicForms/interfaces/IDynamicFormRow";

const inputVariant = "outlined" as const;

export const COUNTRIES = [
  { key: "australia", locale: enAU, code: "AU" },
  { key: "canada", locale: enCA, code: "CA" },
  { key: "france", locale: fr, code: "FR" },
  { key: "ireland", locale: enGB, code: "IE" },
  { key: "malta", locale: enGB, code: "MT" },
  { key: "new_zealand", locale: enNZ, code: "NZ" },
  { key: "spain", locale: es, code: "ES" },
  { key: "united_kingdom", locale: enGB, code: "GB" },
  { key: "united_states", locale: enUS, code: "US" },
];


export const createDetailsFormGroups = (
  t: (key: string, options?: any) => string,
  iCurrencies: ICurrency[],
  languages: ILanguage[],
  timeZones: ITimeZone[],
  isNoCurrency: boolean,
  isNoCountry: boolean,
  getVenueTypes: () => any[] | undefined,
  isLanguageOverwritten?:boolean
): IDynamicFormGroup[] => {
  const rows :IDynamicFormGroup[] =  [
    {
      title: t("forms.details.merchant"),
      description: t("forms.details.merchantSubtitle"),
      formRows: [
        {
          label: "forms.details.ownerName",
          keyName: "owner_name",
          gridColumnOptions: { formCol: 6 },
          inputProperties: { variant: inputVariant },
          Component: FormTextBox,
        },
        {
          label: "forms.details.ownerPhone",
          keyName: "owner_phone",
          gridColumnOptions: { formCol: 6 },
          inputProperties: { variant: inputVariant, required: true },
          Component: FormTextBox,
          registerOptions: {
            required: true,
            pattern: /(.|\s)*\S(.|\s)*/,
          },
        },
        {
          label: "forms.details.ownerEmail",
          keyName: "owner_email",
          gridColumnOptions: { formCol: 6 },
          inputProperties: { variant: inputVariant, required: true },
          Component: FormTextBox,
          registerOptions: {
            required: true,
            pattern: /(.|\s)*\S(.|\s)*/,
          },
        },
      ]
    },
    {
      title: t("forms.details.venue"),
      description: t("forms.details.venueSubtitle"),
      formRows: [
        {
          label: "forms.details.venueName",
          keyName: "venue_name",
          gridColumnOptions: { formCol: 6 },
          inputProperties: { variant: inputVariant, required: true },
          Component: FormTextBox,
          registerOptions: {
            required: true,
            pattern: /(.|\s)*\S(.|\s)*/,
          },
        },
        {
          label: "forms.details.venueType",
          keyName: "venue_type_id",
          gridColumnOptions: { formCol: 6 },
          inputProperties: { variant: inputVariant, required: true },
          Component: FormAutoComplete,
          formProps: {
            selectOptions: getVenueTypes(),
          },
          registerOptions: {
            required: true,
          },
        },
        {
          label: "forms.details.addressBuilding",
          keyName: "address_building",
          gridColumnOptions: { formCol: 6 },
          inputProperties: { variant: inputVariant },
          Component: FormTextBox,
        },
        {
          label: "forms.details.addressNumber",
          keyName: "address_number",
          gridColumnOptions: { formCol: 6 },
          inputProperties: { variant: inputVariant },
          Component: FormTextBox,
        },
        {
          label: "forms.details.venueLocation",
          keyName: "location_name",
          gridColumnOptions: { formCol: 6 },
          inputProperties: { variant: inputVariant, required: true },
          Component: FormTextBox,
          registerOptions: {
            required: true,
            pattern: /(.|\s)*\S(.|\s)*/,
          },
        },
        {
          label: "forms.details.venuePostCode",
          keyName: "address_postcode",
          gridColumnOptions: { formCol: 6 },
          inputProperties: { variant: inputVariant , required: true },
          Component: FormTextBox,
          registerOptions: {
            required: true,
          },
        },
        {
          label: "forms.details.venueCountry",
          keyName: "address_country",
          formProps: {
            selectOptions: COUNTRIES.map((country) => ({
              name: t(`countries.${t(country.key)}`),
              value: country.code,
            })),
          },
          inputProperties: { required: true },
          Component: FormSelect,
          gridColumnOptions: { formCol: 6 },
          registerOptions: {
            required: true,
            pattern: /(.|\s)*\S(.|\s)*/,
          },
          disabled: !isNoCountry,
        },
        {
          label: "forms.details.venueTimeZone",
          keyName: "time_zone_id",
          formProps: {
            selectOptions: timeZones.map((zone) => ({
              name: `(${zone.utc_offset}) ${zone.name}`,
              value: zone.id,
            })),
          },
          Component: FormSelect,
          inputProperties: { required: true },
          gridColumnOptions: { formCol: 6 },
          registerOptions: {
            required: true,
            pattern: /(.|\s)*\S(.|\s)*/,
          },
        },
        {
          label: "forms.details.venuePhone",
          keyName: "contact_phone",
          gridColumnOptions: { formCol: 6 },
          inputProperties: { variant: inputVariant },
          Component: FormTextBox,
        },
        {
          label: "forms.details.venueDescription",
          keyName: "venue_description",
          gridColumnOptions: { formCol: 12 },
          Component: FormTextBox,
          inputProperties: {
            variant: inputVariant,
            multiline: true,
            rows: 4,
          },
        },
        {
          label: "forms.details.language",
          keyName: "language_id",
          Component: FormSelect,
          disabled:isLanguageOverwritten,
          formProps: {
            selectOptions: languages
              ? languages.map((language: ILanguage) => ({
                name: t(`languages.${language.iso_code}`),
                value: language.id,
              }))
              : [],
          },
          inputProperties: { required: true },
          gridColumnOptions: { formCol: 6 },
          registerOptions: {
            required: true,
            pattern: /(.|\s)*\S(.|\s)*/,
          },
        },
        {
          label: "forms.details.currency",
          tooltip: "forms.details.currency.tooltip",
          keyName: "currency_id",
          Component: FormSelect,
          inputProperties: { required: true },
          formProps: {
            selectOptions: iCurrencies
              ? iCurrencies.map((currency: ICurrency) => ({
                name: t(`currencies.${currency.iso_code}`),
                value: currency.id,
              }))
              : [],
          },
          gridColumnOptions: { formCol: 6 },
          disabled: !isNoCurrency,
        },
        ...(isLanguageOverwritten ? [
         {
          label:'',
          keyName:'',
          Component: AlertStatusCard,
          gridColumnOptions: { formCol: 6 as IMateriaulGridOps },
          formProps:{
            alertProps:{severity: "info"},
            alertMessage:
              <>
                {t("groups.language.info")}{" "}
                <b>
                  {t("groups.language.subInfo")}
                </b>
              </>
          }}
        ] : [])
      ]
    },
    {
      title: t("forms.details.vatTitle"),
      // description: t("form.details.vatDescription"),
      formRows: [
        {
          label: "forms.details.vat",
          keyName: "vat_number",
          gridColumnOptions: { formCol: 6 },
          inputProperties: { variant: inputVariant },
          Component: FormTextBox,
          registerOptions: {
            pattern: /^.{5,25}$/,
          },
        },
        {
          label: "forms.details.exo",
          keyName: "exo_number",
          gridColumnOptions: { formCol: 6 },
          inputProperties: { variant: inputVariant },
          Component: FormTextBox,
          registerOptions: {
            pattern: /^.{5,25}$/,
          },
        },
      ]
    }
  ]

  return rows;

}
